import type { FC } from "react";
import { ASSISTANT_AVATAR, SURFACE } from "../../../../../theme/tokens";
import { AssistantAvatarEyes, AssistantAvatarMood, AssistantHumanAccessory, type AssistantHumanAvatar } from "../../assistantAvatarConfig";
import type { AssistantAvatarPointSide } from "../../assistantAvatarGeometry";
import { type AvatarBodyPalette, HAIR_BY_COLOR, SKIN_BY_TONE } from "../../assistantAvatarPalette";
import { AvatarPointingHand, type PointingArmGeometry } from "../shared/AvatarPointingHand";
import { AvatarWaveHand } from "../shared/AvatarWaveHand";
import { HumanAccessory } from "./HumanAccessory";
import { HumanAiMarker } from "./HumanAiMarker";
import { HumanBust } from "./HumanBust";
import { HumanHairBack } from "./HumanHairBack";
import { HumanHairFront } from "./HumanHairFront";
import { HumanHead } from "./HumanHead";
import { HumanStethoscope } from "./HumanStethoscope";

interface HumanFigureProps {
	config: AssistantHumanAvatar;
	mood: AssistantAvatarMood;
	body: AvatarBodyPalette;
	/** Unique, CSS-safe prefix for this instance's gradient and clip ids. */
	idPrefix: string;
	/** Rendered width in px, so the AI marker can keep a legible minimum size. */
	size: number;
	/** Screen side to point toward; the pointing arm replaces the needs-you wave. */
	point: AssistantAvatarPointSide | undefined;
}

// A scrubs sleeve rising from the shoulder out past the portrait disc; bent high enough to clear the AI marker.
const HUMAN_ARM: PointingArmGeometry = { shoulder: { x: 38, y: 100 }, bend: { x: 16, y: 80 }, hand: { x: 8, y: 62 }, handRadius: 7.5, armWidth: 8 };

/** The human style: a stylized portrait in a colored disc, always carrying the AI marker. */
export const HumanFigure: FC<HumanFigureProps> = ({ config, mood, body, idPrefix, size, point }) => {
	const isDone = mood === AssistantAvatarMood.Done;
	const eyes = isDone ? AssistantAvatarEyes.Happy : config.eyes;
	const accessory = isDone && config.accessory === AssistantHumanAccessory.Shades ? AssistantHumanAccessory.None : config.accessory;
	const skin = SKIN_BY_TONE[config.skin_tone];
	const hairColor = HAIR_BY_COLOR[config.hair_color];
	const backdropId = `${idPrefix}-backdrop`;
	const clipId = `${idPrefix}-clip`;
	return (
		<>
			<defs>
				<linearGradient id={backdropId} x1={0} y1={0} x2={0} y2={1}>
					<stop offset={0} stopColor={SURFACE} />
					<stop offset={1} stopColor={body.light} />
				</linearGradient>
				<clipPath id={clipId}>
					<circle cx={60} cy={60} r={56} />
				</clipPath>
			</defs>
			<circle cx={60} cy={60} r={56} fill={`url(#${backdropId})`} />
			<g clipPath={`url(#${clipId})`}>
				<HumanHairBack hair={config.hair} color={hairColor} />
				<HumanBust skin={skin} scrubs={body.main} />
				{accessory === AssistantHumanAccessory.Stethoscope && <HumanStethoscope />}
			</g>
			<HumanHead mood={mood} eyes={eyes} accessory={accessory} facialHair={config.facial_hair} skin={skin} hairInk={hairColor} />
			<HumanHairFront hair={config.hair} color={hairColor} />
			<HumanAccessory accessory={accessory} />
			{point !== undefined && (
				<AvatarPointingHand side={point} geometry={HUMAN_ARM} armColor={body.main} handFill={skin} outline={ASSISTANT_AVATAR.FACE.HAND_OUTLINE_HUMAN} />
			)}
			{mood === AssistantAvatarMood.NeedsYou && point === undefined && (
				<AvatarWaveHand cx={97} cy={86} rx={6.5} ry={8} fill={skin} outline={ASSISTANT_AVATAR.FACE.HAND_OUTLINE_HUMAN} />
			)}
			<HumanAiMarker size={size} />
		</>
	);
};
